import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { updateConnection } from '../../../api/routes/routesApi';
import { validateActionData } from '../../routes/flowMap/validateActionData';
import {
  ConfigTarget,
  actionCanCommitForm,
  actionIsSwitchedOn,
  configFor,
} from './actionSetup';

type Translate = (key: any, options?: any) => string;

// Per-section editing for integration action configurations: stash the edits of
// the section the user is on, validate them client-side, PATCH only what changed.
// Every screen that renders SelectionPanel sections shares this so "Save" means
// the same thing — one action's configuration — wherever it appears.
export function useConfigEditor({
  token,
  t,
  onSaved,
  successMessage,
}: {
  token: string | undefined;
  t: Translate;
  // What the toast says on a successful save — "Connection updated" for an
  // Connection panel, something else on a screen that isn't one.
  successMessage?: string;
  // Re-fetch the integration that was just saved, so the forms rebase on the
  // stored values instead of the ones the browser happens to hold.
  onSaved?: (targetId: string) => Promise<void> | void;
}) {
  // targetId -> actionId -> configuration payload
  const pending = useRef<Record<string, Record<string, any>>>({});
  // Latest form data per action, including the defaults rjsf populates on mount.
  // Saving a never-saved action commits these without the user having to touch a
  // field first, so accepting the defaults is one click.
  const formSnapshot = useRef<Record<string, Record<string, any>>>({});
  const [dirtyIds, setDirtyIds] = useState<Record<string, boolean>>({});
  // Bumping a target's version remounts its forms — used by Cancel (revert) and
  // Save (rebase on the stored configuration).
  const [formVersion, setFormVersion] = useState<Record<string, number>>({});
  // Field-level validation errors from the last Save attempt, in SchemaForm's
  // errors shape: targetId -> actionId -> { field: message }.
  const [formErrors, setFormErrors] = useState<
    Record<string, Record<string, Record<string, string>>>
  >({});

  const anyDirty = Object.keys(dirtyIds).some((k) => dirtyIds[k]);
  const isDirty = (targetId: string) => !!dirtyIds[targetId];
  // Whether one specific section has unsaved edits — what the section-switch
  // guard asks, since saving is per section.
  const isActionDirty = (targetId: string, actionId: string) =>
    !!dirtyIds[targetId] && !!pending.current[targetId]?.[actionId];

  function stashChange(target: ConfigTarget, actionId: string, data: any) {
    const existing = target.configurations?.find(
      (c: any) => c.action?.id === actionId,
    );
    pending.current[target.id] = pending.current[target.id] || {};
    pending.current[target.id][actionId] = existing
      ? { id: existing.id, action: actionId, data }
      : { action: actionId, data };
    setDirtyIds((prev) =>
      prev[target.id] ? prev : { ...prev, [target.id]: true },
    );
  }

  // Called on every rjsf onChange, including the mount-time default population
  // that carries no fieldId.
  function snapshotChange(
    target: ConfigTarget,
    actionId: string,
    data: any,
    fieldId?: string,
  ) {
    formSnapshot.current[target.id] = formSnapshot.current[target.id] || {};
    formSnapshot.current[target.id][actionId] = data;
    if (fieldId) stashChange(target, actionId, data);
  }

  // Seeds the pending stash from the form's current values so Save can commit an
  // action whose only content is its defaults. No-op once the user has edited it.
  function commitDefaults(target: ConfigTarget, action: any) {
    if (!action || !actionCanCommitForm(target, action)) return;
    if (pending.current[target.id]?.[action.id]) return;
    // rjsf only fires its mount-time onChange when it actually changes the form
    // data, so an action whose saved values already satisfy the schema leaves no
    // snapshot behind. Falling back to {} there would submit an empty payload
    // and report every required field as missing while the form shows them
    // filled — the saved configuration is the honest starting point.
    stashChange(
      target,
      action.id,
      formSnapshot.current[target.id]?.[action.id] ??
        configFor(target, action)?.data ??
        {},
    );
  }

  // True when Save should be live even though nothing was edited: the action was
  // never saved, or what is saved is still missing required fields.
  // A switched-off section is committable like any other: "off" is a stored
  // `run_on_schedule: false`, not the absence of a configuration, so the choice
  // survives a reload (product call, GUNDI-5697 review).
  const canCommitDefaults = (target: ConfigTarget | undefined, action: any) =>
    !!target && !!action && actionCanCommitForm(target, action);

  const pendingDataFor = (targetId: string, actionId: string) =>
    pending.current[targetId]?.[actionId]?.data;

  // Unsaved edits win over the saved configuration: switching sections unmounts
  // the form, and rehydrating from the stored config would show stale values
  // while Save still PATCHes the stashed edit.
  const formDataFor = (target: ConfigTarget, action: any) =>
    pendingDataFor(target.id, action.id) ??
    configFor(target, action)?.data ??
    {};

  // Resolves true only when the PATCH landed — the section-switch guard uses
  // this to decide whether it's safe to move on. Pass an actionId to save just
  // that section: the guard keeps one section dirty at a time, but a selection
  // driven by the URL (browser back/forward) slips past it, and Save must still
  // mean the section on screen rather than everything stashed for the account.
  async function save(
    target: ConfigTarget,
    actionId?: string,
  ): Promise<boolean> {
    const stashed = pending.current[target.id];
    if (!stashed || !token) return false;
    const changes = actionId
      ? stashed[actionId]
        ? { [actionId]: stashed[actionId] }
        : undefined
      : stashed;
    if (!changes) return false;
    // Validate against each action's schema before PATCHing — invalid data
    // never leaves the browser.
    // A switched-off section is exempt: the connector never runs it, so its
    // required fields are not work the user owes, and holding the save over
    // them would make the switch impossible to turn off. Everything the user
    // did fill in is still written, so the off state persists.
    const targetErrors: Record<string, Record<string, string>> = {};
    Object.values(changes).forEach((cfg: any) => {
      const action = target.actions.find((a: any) => a.id === cfg.action);
      if (!action?.schema) return;
      if (!actionIsSwitchedOn(action, cfg.data)) return;
      const fieldErrors = validateActionData(
        action.schema,
        cfg.data,
        t,
        action.ui_schema,
      );
      if (Object.keys(fieldErrors).length > 0)
        targetErrors[cfg.action] = fieldErrors;
    });
    // Scoped to what was validated: a save of one section must not wipe the
    // errors another section is still carrying from its own failed save.
    setFormErrors((prev) => ({
      ...prev,
      [target.id]: actionId
        ? {
            ...prev[target.id],
            ...targetErrors,
            [actionId]: targetErrors[actionId] || {},
          }
        : targetErrors,
    }));
    if (Object.keys(targetErrors).length > 0) {
      toast.error(t('Please fix the highlighted fields before saving.'));
      return false;
    }
    try {
      // A 401 is swallowed by the API layer (the login modal handles it) and
      // comes back as undefined — that is not a save, so the edits stay put
      // rather than being cleared behind a "saved" toast.
      const saved = await updateConnection(token, target.id, {
        configurations: Object.values(changes),
      });
      if (!saved) return false;
      toast.success(successMessage || t('Connection updated'));
      // Refresh FIRST, then drop the stash. The form reads the stash before
      // the stored configuration, so clearing it while `target` still holds
      // the pre-save fetch made the field flash its old value ("12" → "11" →
      // "12") until the reload landed.
      await onSaved?.(target.id);
      // Only clear what was actually submitted. The user can keep typing
      // while the PATCH is in flight, and stashChange replaces the entry in
      // place — deleting it unconditionally would drop that newer edit and
      // let the reload remount the value we just saved over it.
      Object.entries(changes).forEach(([id, submitted]) => {
        if (stashed[id] === submitted) delete stashed[id];
      });
      // Anything the user stashed for another section of this account is still
      // unsaved, so the account stays dirty until that is dealt with too.
      const stillPending = Object.keys(stashed).length > 0;
      if (!stillPending) delete pending.current[target.id];
      setDirtyIds((prev) => ({ ...prev, [target.id]: stillPending }));
      setFormVersion((prev) => ({
        ...prev,
        [target.id]: (prev[target.id] || 0) + 1,
      }));
      return true;
    } catch (e) {
      console.error(e);
      toast.error(
        t('Something went wrong. Please try again or contact support.'),
      );
      return false;
    }
  }

  // Scoped the same way as save: with an actionId it reverts just that section
  // and leaves any other stashed section alone.
  function cancel(target: ConfigTarget, actionId?: string) {
    const stashed = pending.current[target.id];
    if (actionId && stashed) delete stashed[actionId];
    const stillPending =
      !!stashed && !!actionId && Object.keys(stashed).length > 0;
    if (!stillPending) delete pending.current[target.id];
    setDirtyIds((prev) => ({ ...prev, [target.id]: stillPending }));
    setFormErrors((prev) => ({
      ...prev,
      [target.id]: actionId ? { ...prev[target.id], [actionId]: {} } : {},
    }));
    setFormVersion((prev) => ({
      ...prev,
      [target.id]: (prev[target.id] || 0) + 1,
    }));
  }

  // Confirming "Discard changes" must actually drop the stash — otherwise the
  // "discarded" edits keep re-prompting and get PATCHed by a later Save.
  function discardAll() {
    const staleIds = Object.keys(dirtyIds).filter((k) => dirtyIds[k]);
    pending.current = {};
    setDirtyIds({});
    setFormErrors({});
    setFormVersion((prev) => {
      const next = { ...prev };
      staleIds.forEach((id) => {
        next[id] = (next[id] || 0) + 1;
      });
      return next;
    });
  }

  return {
    anyDirty,
    isDirty,
    isActionDirty,
    dirtyIds,
    formVersion,
    formErrors,
    stashChange,
    snapshotChange,
    commitDefaults,
    canCommitDefaults,
    formDataFor,
    save,
    cancel,
    discardAll,
  };
}
